import { useState } from 'react';

import { STEUERSAETZE, formatiereEuro, type Position, type Steuersatz } from '../warenkorb';
import { MAX_VORGABE_MENGE } from '../schnellwahl';
import { ZIFFERN_TASTEN, tastaturZuZiffer, ziffernTaste, type ZiffernTaste } from '../ziffern';

/**
 * Tastenfeld des Ziffernblocks (7–9, 4–6, 1–3, 00, 0, ⌫) – gemeinsam für die freie Position und
 * den Bar-Dialog V2. Ohne Zusatzangaben entsteht genau das bisherige Markup.
 */
export function ZiffernTasten(props: {
  onTaste: (t: ZiffernTaste) => void;
  /** Alle Tasten gesperrt (z. B. während der Verarbeitung). */
  gesperrt?: boolean;
  /** Zusätzliche Klasse am Tastenfeld. */
  klasse?: string;
  /** Beschriftung der Tastengruppe für Hilfstechnik (setzt role="group"). */
  beschriftung?: string;
  /** Fokus beim Antippen nicht vom Eingabefeld nehmen (Enter bestätigt dann weiter das Feld). */
  fokusHalten?: boolean;
}) {
  const { onTaste, gesperrt, klasse, beschriftung, fokusHalten } = props;
  return (
    <div
      className={klasse === undefined ? 'ziffern-tasten' : `ziffern-tasten ${klasse}`}
      {...(beschriftung === undefined ? {} : { role: 'group', 'aria-label': beschriftung })}
    >
      {ZIFFERN_TASTEN.map((t) => (
        <button
          key={t}
          type="button"
          {...(gesperrt === undefined ? {} : { disabled: gesperrt })}
          {...(fokusHalten === true
            ? {
                onMouseDown: (e: { preventDefault: () => void }) => {
                  e.preventDefault();
                },
              }
            : {})}
          onClick={() => {
            onTaste(t);
          }}
          aria-label={t === '⌫' ? 'Letzte Ziffer löschen' : t}
        >
          {t}
        </button>
      ))}
    </div>
  );
}

/**
 * Fenster „Menge oder Betrag“ (POS V2 Phase 2, Rahmen D8 `17:3252`): Umschalter Betrag /
 * Stückzahl für die nächste Kachel, Anzeige von rechts eingeschoben, Ziffernblock, Steuersatz
 * (nur Betrag), Bezeichnung und Menge, Abbrechen (Esc) / Hinzufügen (Enter).
 * Physische Tastatur funktioniert ebenso (Ziffern, Rücktaste, Enter, Esc).
 * Eingabe in Cent bleibt beim Verkauf (ein Scan nutzt sie weiterhin als Stückzahl).
 */
export function Ziffernblock(props: {
  onHinzufuegen: (p: Position) => void;
  /** Eingabe in Cent (von rechts eingeschoben) – vom Verkauf gehalten, damit ein Scan sie als Menge nutzen kann. */
  cent: number;
  setCent: (f: (alt: number) => number) => void;
  menge: number;
  setMenge: (n: number) => void;
  /** Stückzahl für die nächste Kachel bzw. den nächsten Scan vormerken (Modus „Stückzahl“). */
  onVormerken?: (stueck: number) => void;
  /** Fenster schließen ohne Übernahme (Esc / Abbrechen). */
  onAbbrechen?: () => void;
}) {
  const { cent, setCent, menge, setMenge } = props;
  const [steuersatz, setSteuersatz] = useState<Steuersatz>('20');
  const [bezeichnung, setBezeichnung] = useState('');
  const [modus, setModus] = useState<'betrag' | 'stueck'>('betrag');
  const stueck = Number.isInteger(cent) && cent >= 1 && cent <= MAX_VORGABE_MENGE ? cent : null;

  function hinzufuegen(): void {
    if (cent <= 0) {
      return;
    }
    props.onHinzufuegen({
      bezeichnung: bezeichnung.trim() === '' ? 'Artikel' : bezeichnung.trim(),
      menge,
      bruttoCent: cent * menge,
      steuersatz,
    });
    setCent(() => 0);
    setMenge(1);
    setBezeichnung('');
  }

  function bestaetigen(): void {
    if (modus === 'betrag') {
      hinzufuegen();
    } else if (stueck !== null) {
      props.onVormerken?.(stueck);
    }
  }

  return (
    <div
      className="ziffernblock menge-betrag-fenster"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          if (e.target instanceof HTMLButtonElement) {
            return; // Enter auf einer Taste löst nur diese Taste aus
          }
          e.preventDefault();
          bestaetigen();
          return;
        }
        if (e.key === 'Escape' && props.onAbbrechen !== undefined) {
          e.preventDefault();
          props.onAbbrechen();
          return;
        }
        const t = tastaturZuZiffer(e.key);
        if (t !== null && !(e.target instanceof HTMLInputElement)) {
          e.preventDefault();
          setCent((c) => ziffernTaste(c, t));
        }
      }}
    >
      <div className="dlg-kopf">
        <h2 id="menge-betrag-titel">Menge oder Betrag</h2>
        <span className="dlg-meta">Tastatur und Ziffernblock</span>
      </div>
      <div className="chips" role="group" aria-label="Eingabe für">
        <button
          type="button"
          className={modus === 'betrag' ? 'aktiv' : ''}
          aria-pressed={modus === 'betrag'}
          onClick={() => {
            setModus('betrag');
          }}
        >
          Betrag
        </button>
        <button
          type="button"
          className={modus === 'stueck' ? 'aktiv' : ''}
          aria-pressed={modus === 'stueck'}
          onClick={() => {
            setModus('stueck');
          }}
        >
          Stückzahl für nächste Kachel
        </button>
      </div>
      <div className="ziffern-haupt">
        <span className="dlg-feldname">
          {modus === 'betrag'
            ? 'Betrag (Eingabe von rechts: 4 5 0 → 4,50 €)'
            : `Stückzahl (1–${String(MAX_VORGABE_MENGE)}) – gilt für die nächste Kachel oder den nächsten Scan`}
        </span>
        <div className="ziffern-anzeige" aria-live="polite">
          {modus === 'betrag' ? `${formatiereEuro(cent)} €` : `${String(cent)}×`}
        </div>
        {modus === 'stueck' && cent > MAX_VORGABE_MENGE && (
          <p className="warnung">Höchstens {MAX_VORGABE_MENGE} Stück</p>
        )}
        <ZiffernTasten
          onTaste={(t) => {
            setCent((c) => ziffernTaste(c, t));
          }}
        />
      </div>
      {modus === 'betrag' && (
        <div className="ziffern-seite">
          <span className="dlg-feldname">Steuersatz (nur für Betrag)</span>
          <div className="chips" role="group" aria-label="Steuersatz">
            {STEUERSAETZE.map((s) => (
              <button
                key={s.satz}
                type="button"
                className={s.satz === steuersatz ? 'aktiv' : ''}
                onClick={() => {
                  setSteuersatz(s.satz);
                }}
              >
                {s.satz} %
              </button>
            ))}
          </div>
          <span className="dlg-feldname">Bezeichnung (sonst „Artikel“)</span>
          <div className="ziffern-bezeichnung">
            <input
              value={bezeichnung}
              onChange={(e) => {
                setBezeichnung(e.target.value);
              }}
              placeholder="Bezeichnung (sonst „Artikel“)"
              aria-label="Bezeichnung"
            />
            <div className="stepper" aria-label="Menge">
              <button
                type="button"
                aria-label="Menge verringern"
                onClick={() => {
                  setMenge(Math.max(1, menge - 1));
                }}
              >
                −
              </button>
              <span>{menge}×</span>
              <button
                type="button"
                aria-label="Menge erhöhen"
                onClick={() => {
                  setMenge(menge + 1);
                }}
              >
                +
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="dlg-fuss">
        {props.onAbbrechen !== undefined && (
          <button
            type="button"
            className="taste zweit"
            data-kuerzel="Esc"
            onClick={props.onAbbrechen}
          >
            Abbrechen
          </button>
        )}
        {modus === 'betrag' ? (
          <button
            type="button"
            className="taste gross"
            data-kuerzel="Enter"
            disabled={cent <= 0}
            onClick={hinzufuegen}
          >
            Hinzufügen
          </button>
        ) : (
          <button
            type="button"
            className="taste gross"
            data-kuerzel="Enter"
            disabled={stueck === null}
            onClick={bestaetigen}
          >
            Für nächste Kachel
          </button>
        )}
      </div>
    </div>
  );
}
